import Papa from 'papaparse';
export type SheetRow = Record<string, string>;
const DEMO_REMOTE_CACHE_KEY = 'onebeat:demo-remote-config-cache';
const DEMO_CONFIG_ID_KEY = 'onebeat:demo-config-id';
const PREVIEW_CONFIG_ID_KEY = 'onebeat:demo-preview-config-id';
type RuntimeOverrides = {
  demoConfigUrl?: string;
  demoConfigUrlLocal?: string;
  sheetsMode?: string;
};
async function loadRuntimeConfig(): Promise<RuntimeOverrides | null> {
  if (typeof window === 'undefined' || import.meta.env.MODE === 'test') {
    return null;
  }
  try {
    const response = await fetch('/runtime-config.json', { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as RuntimeOverrides;
    if (!data || typeof data !== 'object') {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}
function getConfigIdFromUrl() {
  if (typeof window === 'undefined') {
    return '';
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('configId') ?? '';
}
function isPreviewMode() {
  if (typeof window === 'undefined') {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('preview') === '1';
}
function getStoredConfigId() {
  if (typeof window === 'undefined') {
    return '';
  }
  if (isPreviewMode()) {
    return window.sessionStorage.getItem(PREVIEW_CONFIG_ID_KEY) ?? '';
  }
  return window.localStorage.getItem(DEMO_CONFIG_ID_KEY) ?? '';
}
function setStoredConfigId(id: string) {
  if (typeof window === 'undefined') {
    return;
  }
  const normalized = id.trim();
  if (!normalized) {
    return;
  }
  if (isPreviewMode()) {
    window.sessionStorage.setItem(PREVIEW_CONFIG_ID_KEY, normalized);
    return;
  }
  window.localStorage.setItem(DEMO_CONFIG_ID_KEY, normalized);
}
function getRemoteConfigCacheKey() {
  if (isPreviewMode()) {
    return '';
  }
  const currentConfigId = getConfigIdFromUrl() || getStoredConfigId();
  if (currentConfigId) {
    return `${DEMO_REMOTE_CACHE_KEY}:config:${currentConfigId}`;
  }
  return DEMO_REMOTE_CACHE_KEY;
}
function shouldUsePreviewDraft() {
  if (typeof window === 'undefined') {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('previewState') === 'draft';
}
const runtimeOverrides = await loadRuntimeConfig();
const runtimeSheetsMode = runtimeOverrides?.sheetsMode?.trim();
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const runtimeDemoConfigUrl =
  (isLocalhost ? runtimeOverrides?.demoConfigUrlLocal?.trim() : undefined) ||
  runtimeOverrides?.demoConfigUrl?.trim();
const configIdFromUrl = getConfigIdFromUrl();
if (configIdFromUrl) {
  setStoredConfigId(configIdFromUrl);
}
if (configIdFromUrl && typeof window !== 'undefined' && !isPreviewMode()) {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('configId');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  } catch (error) {
    void error;
  }
}
const configId = configIdFromUrl || getStoredConfigId();
export const SHEETS_MODE = runtimeSheetsMode || import.meta.env.VITE_SHEETS_MODE || 'mock';
const buildDemoConfigUrl = (baseUrl: string) => {
  if (!configId) {
    return baseUrl;
  }
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const url = new URL(baseUrl, origin);
    url.searchParams.set('configId', configId);
    if (shouldUsePreviewDraft()) {
      url.searchParams.set('preview', '1');
    }
    return url.toString();
  } catch (error) {
    return baseUrl;
  }
};
export const DEMO_CONFIG_URL = buildDemoConfigUrl(
  runtimeDemoConfigUrl ||
    import.meta.env.VITE_DEMO_CONFIG_URL ||
    'http://localhost:4100/api/demo-config',
);
type DemoConfig = {
  id?: string;
  profile?: {
    organizationName?: string;
    userName?: string;
    organizationInitials?: string;
    theme?: string;
  };
  moduleOrder?: string[];
  replenishmentSheetsId?: string;
  allocationsSheetsId?: string;
  allocations2SheetsId?: string;
  allocations2PackComponentsSheetsId?: string;
  allocations2PackDistributionSheetsId?: string;
  inventorySheetsId?: string;
  purchasingSheetsId?: string;
  purchasingRecommendationsSheetsId?: string;
  purchasingOrdersSheetsId?: string;
  purchasingInSeasonReactionSheetsId?: string;
  purchasingInSeasonReactionSheetsUrl?: string;
  liquidationSheetsId?: string;
  liquidationSheetsUrl?: string;
  liquidationEnabled?: boolean;
  otbExecutionSheetsId?: string;
  otbExecutionSheetsUrl?: string;
  otbExecutionEnabled?: boolean;
  rulesEngineEnabled?: boolean;
  rulesEngineScopeSheetsId?: string;
  rulesEngineScopeSheetsUrl?: string;
  storeTransferSheetsId?: string;
  newReplenishmentSheetsId?: string;
  replenishmentSheetsUrl?: string;
  allocationsSheetsUrl?: string;
  allocations2SheetsUrl?: string;
  allocations2PackComponentsSheetsUrl?: string;
  allocations2PackDistributionSheetsUrl?: string;
  inventorySheetsUrl?: string;
  purchasingSheetsUrl?: string;
  purchasingRecommendationsSheetsUrl?: string;
  purchasingOrdersSheetsUrl?: string;
  storeTransferSheetsUrl?: string;
  newReplenishmentSheetsUrl?: string;
  specialEventsSheetsId?: string;
  specialEventsSheetsUrl?: string;
  specialEventsSkuDetailsSheetsId?: string;
  specialEventsSkuDetailsSheetsUrl?: string;
  cfoPanelSheetsId?: string;
  cfoPanelSheetsUrl?: string;
  ceoPanelSheetsId?: string;
  ceoPanelSheetsUrl?: string;
  ccoPanelSheetsId?: string;
  ccoPanelSheetsUrl?: string;
  ccoPanelSheets2Id?: string;
  ccoPanelSheets2Url?: string;
  ccoPanelSheets3Id?: string;
  ccoPanelSheets3Url?: string;
  ccoPanelSheets4Id?: string;
  ccoPanelSheets4Url?: string;
  ccoPanelSheets5Id?: string;
  ccoPanelSheets5Url?: string;
  replenishmentEnabled?: boolean;
  allocationsEnabled?: boolean;
  allocations2Enabled?: boolean;
  inventoryEnabled?: boolean;
  inventory2Enabled?: boolean;
  analyticsEnabled?: boolean;
  purchasingEnabled?: boolean;
  storeTransferEnabled?: boolean;
  specialEventsEnabled?: boolean;
  planningAssistantEnabled?: boolean;
  betaReportsEnabled?: boolean;
  newReplenishmentEnabled?: boolean;
  cfoPanelEnabled?: boolean;
  ceoPanelEnabled?: boolean;
  ccoPanelEnabled?: boolean;
  updatedAt?: string;
  version?: number;
  moduleImages?: Record<string, string>;
  modules?: Record<string, unknown>[];
};
const shouldLoadRemoteConfig =
  typeof window !== 'undefined' &&
  import.meta.env.MODE !== 'test' &&
  !(import.meta as any).env?.VITEST;
function loadRemoteConfigCache(): DemoConfig | null {
  if (typeof window === 'undefined' || isPreviewMode()) {
    return null;
  }
  const raw = window.localStorage.getItem(getRemoteConfigCacheKey());
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as DemoConfig;
  } catch (error) {
    return null;
  }
}
function saveRemoteConfigCache(config: DemoConfig) {
  if (typeof window === 'undefined' || isPreviewMode()) {
    return;
  }
  window.localStorage.setItem(getRemoteConfigCacheKey(), JSON.stringify(config));
}
function isSameRemoteConfig(next: DemoConfig, current: DemoConfig | null) {
  if (!current) {
    return false;
  }
  if (next.version != null && current.version != null) {
    return next.version === current.version;
  }
  if (next.updatedAt && current.updatedAt) {
    return next.updatedAt === current.updatedAt;
  }
  return JSON.stringify(next) === JSON.stringify(current);
}
async function loadRemoteConfig(cached: DemoConfig | null): Promise<DemoConfig | null> {
  try {
    const response = await fetch(DEMO_CONFIG_URL, { method: 'GET' });
    if (!response.ok) {
      return cached;
    }
    const data = (await response.json()) as DemoConfig;
    if (!data || typeof data !== 'object') {
      return cached;
    }
    if (!isSameRemoteConfig(data, cached)) {
      saveRemoteConfigCache(data);
    }
    return data;
  } catch (error) {
    return cached;
  }
}
function isDemoDebugEnabled() {
  if (typeof window === 'undefined') {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('debug') === '1';
}
const cachedRemoteConfig = loadRemoteConfigCache();
const remoteConfig = shouldLoadRemoteConfig
  ? await loadRemoteConfig(cachedRemoteConfig)
  : cachedRemoteConfig;
let runtimeConfig = remoteConfig;
if (isDemoDebugEnabled()) {
  console.debug('[demo-config] preview mode:', isPreviewMode());
  console.debug('[demo-config] preview draft requested:', shouldUsePreviewDraft());
  console.debug('[demo-config] remote config:', remoteConfig);
  console.debug('[demo-config] using:', runtimeConfig);
}
let runtimeConfigMeta =
  runtimeConfig && (runtimeConfig.updatedAt || runtimeConfig.version != null)
    ? {
        updatedAt: runtimeConfig.updatedAt ?? null,
        version: runtimeConfig.version ?? null,
      }
    : null;
export const SHEET_NAMES = {
  ceoCashCycleFlat: import.meta.env.VITE_SHEETS_CEO_CASH_CYCLE_FLAT ?? 'Cash - Cycle flat table',
  replenishmentFlat: import.meta.env.VITE_SHEETS_REPLENISHMENT_FLAT ?? 'replenishment-flat',
  replenishmentLocations:
    import.meta.env.VITE_SHEETS_REPLENISHMENT_LOCATIONS ?? 'replenishment-locations',
  replenishmentProducts:
    import.meta.env.VITE_SHEETS_REPLENISHMENT_PRODUCTS ?? 'replenishment-products',
  replenishmentInventories:
    import.meta.env.VITE_SHEETS_REPLENISHMENT_INVENTORIES ?? 'replenishment-inventories',
  replenishmentOverview:
    import.meta.env.VITE_SHEETS_REPLENISHMENT_OVERVIEW ?? 'replenishment-overview',
  replenishmentProductDetails:
    import.meta.env.VITE_SHEETS_REPLENISHMENT_PRODUCT_DETAILS ?? 'replenishment-product-details',
  replenishmentProductSkuDetails:
    import.meta.env.VITE_SHEETS_REPLENISHMENT_PRODUCT_SKU_DETAILS ??
    'replenishment-product-sku-details',
  replenishmentProductSkuDetailsInLocation:
    import.meta.env.VITE_SHEETS_REPLENISHMENT_PRODUCT_SKU_DETAILS_IN_LOCATION ??
    'replenishment-product-sku-details-in-location',
  replenishmentProductLocations:
    import.meta.env.VITE_SHEETS_REPLENISHMENT_PRODUCT_LOCATIONS ??
    'replenishment-product-locations',
  replenishmentLocationProducts:
    import.meta.env.VITE_SHEETS_REPLENISHMENT_LOCATION_PRODUCTS ??
    'replenishment-location-products',
  insufficientSummary: import.meta.env.VITE_SHEETS_INSUFFICIENT_SUMMARY ?? 'insufficient-summary',
  insufficientOverview:
    import.meta.env.VITE_SHEETS_INSUFFICIENT_OVERVIEW ?? 'insufficient-overview',
  allocationsFlat: import.meta.env.VITE_SHEETS_ALLOCATIONS_FLAT ?? 'allocations-flat',
  allocations2Flat: import.meta.env.VITE_SHEETS_ALLOCATIONS2_FLAT ?? 'allocations-2-flat',
  allocations2PackComponents:
    import.meta.env.VITE_SHEETS_ALLOCATIONS2_PACK_COMPONENTS ?? 'allocations-2-pack-components',
  allocations2PackDistribution:
    import.meta.env.VITE_SHEETS_ALLOCATIONS2_PACK_DISTRIBUTION ?? 'allocations-2-pack-distribution',
  inventoryFlat: import.meta.env.VITE_SHEETS_INVENTORY_FLAT ?? 'inventory-flat',
  purchasingRecommendations:
    import.meta.env.VITE_SHEETS_PURCHASING_RECOMMENDATIONS ?? 'purchasing-recommendations',
  purchasingOrders: import.meta.env.VITE_SHEETS_PURCHASING_ORDERS ?? 'purchasing-orders',
  storeTransferFlat: import.meta.env.VITE_SHEETS_STORE_TRANSFER_FLAT ?? 'store-transfer-flat',
  newReplenishmentFlat:
    import.meta.env.VITE_SHEETS_NEW_REPLENISHMENT_FLAT ?? 'new-replenishment-flat',
  specialEventsEvents: import.meta.env.VITE_SHEETS_SPECIAL_EVENTS_EVENTS ?? 'Events',
  specialEventsSkuLocations:
    import.meta.env.VITE_SHEETS_SPECIAL_EVENTS_SKU_LOCATIONS ?? 'SKU-Locations',
  cfoPanelIndicators: import.meta.env.VITE_SHEETS_CFO_PANEL ?? 'Sheet1',
  liquidationFlat: import.meta.env.VITE_SHEETS_LIQUIDATION_FLAT ?? 'liquidation-flat',
  otbExecutionFlat: import.meta.env.VITE_SHEETS_OTB_EXECUTION_FLAT ?? 'otb-execution-flat',
  purchasingInSeasonReaction:
    import.meta.env.VITE_SHEETS_PURCHASING_IN_SEASON_REACTION ?? 'in-season-reaction',
} as const;
const SHEET_ALIASES: Record<string, string[]> = {
  'Cash - Cycle flat table': [
    'Cash Cycle flat table',
    'cash-cycle-flat-table',
    'cash cycle flat table',
    'Sheet1',
    'sheet1',
  ],
  'replenishment-flat': ['rpl-flat-table', 'rpl_flat_table_v1'],
  'replenishment-product-sku-details': ['rpl-prod-sku-details'],
  'replenishment-product-sku-details-in-location': ['rpl-prod-sku-details-loc'],
  'allocations-flat': ['Allocation flat demo UI - Sheet1', 'Sheet1', 'sheet1'],
  'allocations-2-flat': ['Sheet1', 'sheet1', 'allocations-2-flat'],
  'allocations-2-pack-components': [
    'Sheet1',
    'sheet1',
    'pack-components',
    'Pack Components',
    'allocations-2-pack-components',
  ],
  'allocations-2-pack-distribution': [
    'Sheet1',
    'sheet1',
    'pack-distribution',
    'Pack Distribution',
    'allocations-2-pack-distribution',
  ],
  'inventory-flat': ['Inventory flat demo UI - Sheet1', 'Sheet1', 'sheet1'],
  'purchasing-recommendations': [
    'Purchasing flat tables - Recommendations',
    'Recommendations',
    'recommendations',
  ],
  'purchasing-orders': ['Purchasing flat tables - Orders', 'Orders', 'orders'],
  'store-transfer-flat': ['Store Transfers data for static demo - Sheet1', 'Sheet1', 'sheet1'],
  'new-replenishment-flat': ['new-replenishment-flat', 'Sheet1', 'sheet1'],
  Events: ['events', 'Events', 'events-flat'],
  'SKU-Locations': ['sku-locations', 'SKU-Locations', 'Sku-Locations', 'sku_locations'],
  'liquidation-flat': ['Sheet1', 'sheet1', 'liquidation-flat'],
  'otb-execution-flat': ['Sheet1', 'sheet1', 'otb-execution-flat'],
  'in-season-reaction': ['In Season Reaction', 'in-season-reaction', 'Sheet1', 'sheet1'],
};
const sheetCache = new Map<string, Promise<unknown[]>>();
type SheetsContext =
  | 'replenishment'
  | 'new-replenishment'
  | 'allocations'
  | 'allocations-2'
  | 'inventory'
  | 'inventory-2'
  | 'rules-engine'
  | 'purchasing'
  | 'store-transfer'
  | 'special-events'
  | 'cfo-panel'
  | 'ceo-panel'
  | 'cco-panel'
  | 'liquidation'
  | 'otb-execution';

export function parseSheetIdFromUrl(url: string) {
  if (!url) {
    return '';
  }
  const match = url.match(/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  return match?.[1] ?? '';
}
function getRuntimeSheetsId(context: SheetsContext) {
  const remote = runtimeConfig;
  if (context === 'ceo-panel') {
    return remote?.ceoPanelSheetsId || parseSheetIdFromUrl(remote?.ceoPanelSheetsUrl ?? '') || '';
  }
  if (context === 'cco-panel') {
    return remote?.ccoPanelSheetsId || parseSheetIdFromUrl(remote?.ccoPanelSheetsUrl ?? '') || '';
  }
  if (context === 'cfo-panel') {
    return remote?.cfoPanelSheetsId || parseSheetIdFromUrl(remote?.cfoPanelSheetsUrl ?? '') || '';
  }
  if (context === 'new-replenishment') {
    return (
      remote?.newReplenishmentSheetsId ||
      parseSheetIdFromUrl(remote?.newReplenishmentSheetsUrl ?? '') ||
      ''
    );
  }
  if (context === 'allocations') {
    return remote?.allocationsSheetsId || '';
  }
  if (context === 'allocations-2') {
    return (
      remote?.allocations2SheetsId || parseSheetIdFromUrl(remote?.allocations2SheetsUrl ?? '') || ''
    );
  }
  if (context === 'inventory') {
    return remote?.inventorySheetsId || '';
  }
  if (context === 'inventory-2') {
    return remote?.inventorySheetsId || parseSheetIdFromUrl(remote?.inventorySheetsUrl ?? '') || '';
  }
  if (context === 'rules-engine') {
    return (
      remote?.rulesEngineScopeSheetsId ||
      parseSheetIdFromUrl(remote?.rulesEngineScopeSheetsUrl ?? '') ||
      ''
    );
  }
  if (context === 'purchasing') {
    return (
      remote?.purchasingRecommendationsSheetsId ||
      remote?.purchasingOrdersSheetsId ||
      remote?.purchasingSheetsId ||
      ''
    );
  }
  if (context === 'store-transfer') {
    return (
      remote?.storeTransferSheetsId ||
      parseSheetIdFromUrl(remote?.storeTransferSheetsUrl ?? '') ||
      ''
    );
  }
  if (context === 'special-events') {
    return (
      remote?.specialEventsSheetsId ||
      parseSheetIdFromUrl(remote?.specialEventsSheetsUrl ?? '') ||
      ''
    );
  }
  if (context === 'liquidation') {
    return (
      remote?.liquidationSheetsId || parseSheetIdFromUrl(remote?.liquidationSheetsUrl ?? '') || ''
    );
  }
  if (context === 'otb-execution') {
    return (
      remote?.otbExecutionSheetsId || parseSheetIdFromUrl(remote?.otbExecutionSheetsUrl ?? '') || ''
    );
  }
  return remote?.replenishmentSheetsId || '';
}
function parseValue(value: string | null | undefined) {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed === 'true') {
    return true;
  }
  if (trimmed === 'false') {
    return false;
  }
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return JSON.parse(trimmed);
    } catch (error) {
      return trimmed;
    }
  }
  const asNumber = Number(trimmed);
  if (!Number.isNaN(asNumber) && trimmed !== '') {
    return asNumber;
  }
  return trimmed;
}
function normalizeRow(row: SheetRow) {
  const result: Record<string, unknown> = {};
  Object.entries(row).forEach(([key, value]) => {
    if (!key) {
      return;
    }
    result[key.trim()] = parseValue(value);
  });
  return result;
}
function buildSheetCsvUrl(sheetId: string, sheetName: string) {
  const encodedSheet = encodeURIComponent(sheetName);
  return `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv&sheet=${encodedSheet}`;
}
export function isSheetsMode(context: SheetsContext = 'replenishment') {
  const remote = runtimeConfig;
  const runtimeId = getRuntimeSheetsId(context);
  const remoteId =
    context === 'new-replenishment'
      ? remote?.newReplenishmentSheetsId ||
        parseSheetIdFromUrl(remote?.newReplenishmentSheetsUrl ?? '')
      : context === 'allocations'
        ? remote?.allocationsSheetsId
        : context === 'allocations-2'
          ? remote?.allocations2SheetsId || parseSheetIdFromUrl(remote?.allocations2SheetsUrl ?? '')
          : context === 'inventory' || context === 'inventory-2'
            ? remote?.inventorySheetsId
            : context === 'rules-engine'
              ? remote?.rulesEngineScopeSheetsId ||
                parseSheetIdFromUrl(remote?.rulesEngineScopeSheetsUrl ?? '')
              : context === 'purchasing'
                ? remote?.purchasingRecommendationsSheetsId ||
                  parseSheetIdFromUrl(remote?.purchasingRecommendationsSheetsUrl ?? '') ||
                  remote?.purchasingOrdersSheetsId ||
                  parseSheetIdFromUrl(remote?.purchasingOrdersSheetsUrl ?? '') ||
                  remote?.purchasingSheetsId ||
                  parseSheetIdFromUrl(remote?.purchasingSheetsUrl ?? '')
                : context === 'store-transfer'
                  ? remote?.storeTransferSheetsId ||
                    parseSheetIdFromUrl(remote?.storeTransferSheetsUrl ?? '')
                  : context === 'special-events'
                    ? remote?.specialEventsSheetsId ||
                      parseSheetIdFromUrl(remote?.specialEventsSheetsUrl ?? '')
                    : context === 'cfo-panel'
                      ? remote?.cfoPanelSheetsId ||
                        parseSheetIdFromUrl(remote?.cfoPanelSheetsUrl ?? '')
                      : context === 'ceo-panel'
                        ? remote?.ceoPanelSheetsId ||
                          parseSheetIdFromUrl(remote?.ceoPanelSheetsUrl ?? '')
                        : context === 'cco-panel'
                          ? remote?.ccoPanelSheetsId ||
                            parseSheetIdFromUrl(remote?.ccoPanelSheetsUrl ?? '')
                          : context === 'liquidation'
                            ? remote?.liquidationSheetsId ||
                              parseSheetIdFromUrl(remote?.liquidationSheetsUrl ?? '')
                            : context === 'otb-execution'
                              ? remote?.otbExecutionSheetsId ||
                                parseSheetIdFromUrl(remote?.otbExecutionSheetsUrl ?? '')
                              : remote?.replenishmentSheetsId;
  return Boolean(runtimeId) && (SHEETS_MODE === 'sheets' || Boolean(remoteId));
}
export function hasSheetsId(context: SheetsContext = 'replenishment') {
  return Boolean(getRuntimeSheetsId(context));
}
export function hasRemoteSheetsId(context: SheetsContext = 'replenishment') {
  const remote = runtimeConfig;
  if (context === 'ceo-panel') {
    return Boolean(
      remote?.ceoPanelSheetsId || parseSheetIdFromUrl(remote?.ceoPanelSheetsUrl ?? ''),
    );
  }
  if (context === 'cco-panel') {
    return Boolean(
      remote?.ccoPanelSheetsId || parseSheetIdFromUrl(remote?.ccoPanelSheetsUrl ?? ''),
    );
  }
  if (context === 'new-replenishment') {
    return Boolean(
      remote?.newReplenishmentSheetsId ||
      parseSheetIdFromUrl(remote?.newReplenishmentSheetsUrl ?? ''),
    );
  }
  if (context === 'allocations') {
    return Boolean(
      remote?.allocationsSheetsId || parseSheetIdFromUrl(remote?.allocationsSheetsUrl ?? ''),
    );
  }
  if (context === 'allocations-2') {
    return Boolean(
      remote?.allocations2SheetsId || parseSheetIdFromUrl(remote?.allocations2SheetsUrl ?? ''),
    );
  }
  if (context === 'inventory' || context === 'inventory-2') {
    return Boolean(
      remote?.inventorySheetsId || parseSheetIdFromUrl(remote?.inventorySheetsUrl ?? ''),
    );
  }
  if (context === 'rules-engine') {
    return Boolean(
      remote?.rulesEngineScopeSheetsId ||
        parseSheetIdFromUrl(remote?.rulesEngineScopeSheetsUrl ?? ''),
    );
  }
  if (context === 'purchasing') {
    return Boolean(
      remote?.purchasingRecommendationsSheetsId ||
      parseSheetIdFromUrl(remote?.purchasingRecommendationsSheetsUrl ?? '') ||
      remote?.purchasingSheetsId ||
      parseSheetIdFromUrl(remote?.purchasingSheetsUrl ?? ''),
    );
  }
  if (context === 'store-transfer') {
    return Boolean(
      remote?.storeTransferSheetsId || parseSheetIdFromUrl(remote?.storeTransferSheetsUrl ?? ''),
    );
  }
  if (context === 'special-events') {
    return Boolean(
      remote?.specialEventsSheetsId || parseSheetIdFromUrl(remote?.specialEventsSheetsUrl ?? ''),
    );
  }
  if (context === 'cfo-panel') {
    return Boolean(
      remote?.cfoPanelSheetsId || parseSheetIdFromUrl(remote?.cfoPanelSheetsUrl ?? ''),
    );
  }
  if (context === 'liquidation') {
    return Boolean(
      remote?.liquidationSheetsId || parseSheetIdFromUrl(remote?.liquidationSheetsUrl ?? ''),
    );
  }
  if (context === 'otb-execution') {
    return Boolean(
      remote?.otbExecutionSheetsId || parseSheetIdFromUrl(remote?.otbExecutionSheetsUrl ?? ''),
    );
  }
  return Boolean(
    remote?.replenishmentSheetsId || parseSheetIdFromUrl(remote?.replenishmentSheetsUrl ?? ''),
  );
}
export function getPurchasingSheetIds() {
  const remote = runtimeConfig;
  const recommendationsId =
    remote?.purchasingRecommendationsSheetsId ||
    parseSheetIdFromUrl(remote?.purchasingRecommendationsSheetsUrl ?? '') ||
    '';
  const ordersId =
    remote?.purchasingOrdersSheetsId ||
    parseSheetIdFromUrl(remote?.purchasingOrdersSheetsUrl ?? '') ||
    '';
  const inSeasonReactionId =
    remote?.purchasingInSeasonReactionSheetsId ||
    parseSheetIdFromUrl(remote?.purchasingInSeasonReactionSheetsUrl ?? '') ||
    '';
  const fallbackId =
    remote?.purchasingSheetsId || parseSheetIdFromUrl(remote?.purchasingSheetsUrl ?? '') || '';
  return {
    recommendationsId: recommendationsId || fallbackId,
    ordersId: ordersId || fallbackId,
    inSeasonReactionId,
  };
}
function getConfiguredSheetId(idValue?: string, urlValue?: string) {
  return idValue || parseSheetIdFromUrl(urlValue ?? '') || '';
}
export function getSpecialEventsSheetIds() {
  const remote = runtimeConfig;
  const eventsId = getConfiguredSheetId(
    remote?.specialEventsSheetsId,
    remote?.specialEventsSheetsUrl,
  );
  const skuDetailsId = getConfiguredSheetId(
    remote?.specialEventsSkuDetailsSheetsId,
    remote?.specialEventsSkuDetailsSheetsUrl,
  );
  return {
    eventsId,
    skuDetailsId: skuDetailsId || eventsId,
  };
}
export function getAllocations2SheetIds() {
  const remote = runtimeConfig;
  return {
    flatId: getConfiguredSheetId(remote?.allocations2SheetsId, remote?.allocations2SheetsUrl),
    packComponentsId: getConfiguredSheetId(
      remote?.allocations2PackComponentsSheetsId,
      remote?.allocations2PackComponentsSheetsUrl,
    ),
    packDistributionId: getConfiguredSheetId(
      remote?.allocations2PackDistributionSheetsId,
      remote?.allocations2PackDistributionSheetsUrl,
    ),
  };
}
export function hasPurchasingSheetIds() {
  const { recommendationsId, ordersId } = getPurchasingSheetIds();
  return Boolean(recommendationsId || ordersId);
}
export function getModuleOrder() {
  return runtimeConfig?.moduleOrder ?? null;
}
function normalizeModuleKey(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');
}
function normalizeModuleImageUrl(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (!trimmed.includes('drive.google.com') && !trimmed.includes('googleusercontent.com')) {
    return trimmed;
  }
  const pathMatch = trimmed.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (pathMatch?.[1]) {
    return `https://lh3.googleusercontent.com/d/${pathMatch[1]}`;
  }
  try {
    const url = new URL(trimmed);
    const queryId = url.searchParams.get('id');
    if (queryId) {
      return `https://lh3.googleusercontent.com/d/${queryId}`;
    }
  } catch (error) {
    void error;
  }
  return trimmed;
}
export function getModuleImage(
  context:
    | 'ceo-panel'
    | 'cfo-panel'
    | 'cco-panel'
    | 'coo-panel'
    | 'inventory'
    | 'replenishment'
    | 'new-replenishment'
    | 'allocations'
    | 'allocations-2'
    | 'store-transfer'
    | 'purchasing'
    | 'special-events'
    | 'analytics'
    | 'rules-engine',
) {
  const remote = runtimeConfig as Record<string, unknown> | null;
  if (!remote) {
    return '';
  }

  const directKeysByContext: Record<string, string[]> = {
    'ceo-panel': ['ceoPanelImage', 'ceoImage'],
    'cfo-panel': ['cfoPanelImage', 'cfoImage'],
    'cco-panel': ['ccoPanelImage', 'cooPanelImage', 'ccoImage', 'cooImage'],
    'coo-panel': ['cooPanelImage', 'ccoPanelImage', 'cooImage', 'ccoImage'],
    inventory: ['inventoryImage'],
    replenishment: ['replenishmentImage'],
    'new-replenishment': ['newReplenishmentImage'],
    allocations: ['allocationsImage'],
    'allocations-2': ['allocations2Image'],
    'store-transfer': ['storeTransferImage'],
    purchasing: ['purchasingImage'],
    'special-events': ['specialEventsImage'],
    analytics: ['analyticsImage'],
    'rules-engine': ['rulesEngineImage', 'automationsImage'],
  };
  for (const key of directKeysByContext[context] ?? []) {
    const value = remote[key];
    if (typeof value === 'string' && value.trim()) {
      return normalizeModuleImageUrl(value);
    }
  }

  const moduleImages = remote.moduleImages;
  if (moduleImages && typeof moduleImages === 'object') {
    const normalizedMap = moduleImages as Record<string, unknown>;
    const candidates = [
      context,
      context.replace('coo-panel', 'cco-panel'),
      context.replace('cco-panel', 'coo-panel'),
    ];
    for (const candidate of candidates) {
      const value = normalizedMap[candidate];
      if (typeof value === 'string' && value.trim()) {
        return normalizeModuleImageUrl(value);
      }
    }
  }

  const modules = remote.modules;
  if (Array.isArray(modules)) {
    const normalizedContext = normalizeModuleKey(context);
    const altContext =
      normalizedContext === 'coo-panel'
        ? 'cco-panel'
        : normalizedContext === 'cco-panel'
          ? 'coo-panel'
          : normalizedContext;
    for (const item of modules) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Record<string, unknown>;
      const rawKey =
        (typeof row.key === 'string' && row.key) ||
        (typeof row.id === 'string' && row.id) ||
        (typeof row.module === 'string' && row.module) ||
        (typeof row.name === 'string' && row.name) ||
        '';
      const normalizedKey = normalizeModuleKey(rawKey);
      if (normalizedKey !== normalizedContext && normalizedKey !== altContext) continue;
      const image =
        (typeof row.image === 'string' && row.image) ||
        (typeof row.icon === 'string' && row.icon) ||
        (typeof row.url === 'string' && row.url) ||
        '';
      if (image) {
        return normalizeModuleImageUrl(image);
      }
    }
  }

  return '';
}
export function getStoreTransferSheetId() {
  const remote = runtimeConfig;
  return (
    remote?.storeTransferSheetsId || parseSheetIdFromUrl(remote?.storeTransferSheetsUrl ?? '') || ''
  );
}
export function hasAllocationsSheetsId() {
  return hasSheetsId('allocations');
}
export function hasInventorySheetsId() {
  return hasSheetsId('inventory');
}
export async function getSheetRows<T = Record<string, unknown>>(
  sheetName: string,
  context: SheetsContext = 'replenishment',
  sheetIdOverride?: string,
): Promise<T[]> {
  if (!sheetIdOverride && !isSheetsMode(context)) {
    return [];
  }
  const cacheKey = `${context}:${sheetName}:${sheetIdOverride ?? ''}`;
  if (!sheetCache.has(cacheKey)) {
    const loader = (async () => {
      const candidates = [sheetName, ...(SHEET_ALIASES[sheetName] ?? [])].filter(Boolean);
      const sheetId = sheetIdOverride || getRuntimeSheetsId(context);
      const attemptedUrls: string[] = [];
      let sawHtml = false;
      let sawParseError = false;
      const parseCsv = async (url: string, candidate: string) => {
        const response = await fetch(url);
        if (!response.ok) {
          if (context === 'store-transfer') {
            attemptedUrls.push(`${url} (status ${response.status})`);
          }
          return null;
        }
        const csv = await response.text();
        if (context === 'store-transfer' && /<html|<!doctype/i.test(csv)) {
          sawHtml = true;
          attemptedUrls.push(`${url} (html)`);
          return null;
        }
        const parsed = Papa.parse<SheetRow>(csv, {
          header: true,
          skipEmptyLines: true,
        });
        if (parsed.errors.length) {
          if (context === 'store-transfer') {
            sawParseError = true;
            attemptedUrls.push(`${url} (parse error)`);
            return null;
          }
          const first = parsed.errors[0];
          throw new Error(`Failed to parse sheet "${candidate}": ${first.message}`);
        }
        return parsed.data.map((row) => normalizeRow(row)) as T[];
      };
      for (const candidate of candidates) {
        if (!sheetId) {
          continue;
        }
        const url = buildSheetCsvUrl(sheetId, candidate);
        const parsed = await parseCsv(url, candidate);
        if (parsed) {
          return parsed;
        }
      }
      if (context === 'store-transfer' && sheetId) {
        const url = `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv`;
        const parsed = await parseCsv(url, 'default');
        if (parsed) {
          return parsed;
        }
        const gvizDefaultParsed = await parseCsv(`${url}&gid=0`, 'default-gid');
        if (gvizDefaultParsed) {
          return gvizDefaultParsed;
        }
        const exportUrl = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;
        const exportParsed = await parseCsv(exportUrl, 'export');
        if (exportParsed) {
          return exportParsed;
        }
        const exportGidParsed = await parseCsv(`${exportUrl}&gid=0`, 'export-gid');
        if (exportGidParsed) {
          return exportGidParsed;
        }
        console.warn('[store-transfer] sheet load failed', {
          sheetId,
          attemptedUrls,
          sawHtml,
          sawParseError,
        });
      }
      throw new Error(`Failed to load sheet "${sheetName}"`);
    })();
    sheetCache.set(cacheKey, loader);
  }
  return (await sheetCache.get(cacheKey)) as T[];
}
export async function getAllocationsSheetRows<T = Record<string, unknown>>(
  sheetName: string,
): Promise<T[]> {
  return getSheetRows<T>(sheetName, 'allocations');
}
export async function getAllocations2SheetRows<T = Record<string, unknown>>(
  sheetName: string,
  sheetIdOverride?: string,
): Promise<T[]> {
  return getSheetRows<T>(sheetName, 'allocations-2', sheetIdOverride);
}
export function getRemoteDemoConfig() {
  return runtimeConfig;
}
export function getRemoteDemoConfigMeta() {
  return runtimeConfigMeta;
}
export function isModuleEnabled(
  context:
    | 'replenishment'
    | 'new-replenishment'
    | 'allocations'
    | 'allocations-2'
    | 'inventory'
    | 'inventory-2'
    | 'analytics'
    | 'purchasing'
    | 'store-transfer'
    | 'special-events'
    | 'cfo-panel'
    | 'ceo-panel'
    | 'cco-panel'
    | 'planning-assistant'
    | 'beta-reports'
    | 'liquidation'
    | 'otb-execution'
    | 'rules-engine',
) {
  if (context === 'replenishment') {
    return runtimeConfig?.replenishmentEnabled ?? true;
  }
  if (context === 'new-replenishment') {
    return runtimeConfig?.newReplenishmentEnabled ?? true;
  }
  if (context === 'allocations') {
    return runtimeConfig?.allocationsEnabled ?? true;
  }
  if (context === 'allocations-2') {
    return runtimeConfig?.allocations2Enabled ?? true;
  }
  if (context === 'inventory') {
    return runtimeConfig?.inventoryEnabled ?? true;
  }
  if (context === 'inventory-2') {
    return runtimeConfig?.inventory2Enabled ?? true;
  }
  if (context === 'analytics') {
    return runtimeConfig?.analyticsEnabled ?? true;
  }
  if (context === 'purchasing') {
    return runtimeConfig?.purchasingEnabled ?? true;
  }
  if (context === 'store-transfer') {
    return runtimeConfig?.storeTransferEnabled ?? true;
  }
  if (context === 'special-events') {
    return runtimeConfig?.specialEventsEnabled ?? true;
  }
  if (context === 'cfo-panel') {
    return runtimeConfig?.cfoPanelEnabled ?? true;
  }
  if (context === 'ceo-panel') {
    return runtimeConfig?.ceoPanelEnabled ?? true;
  }
  if (context === 'cco-panel') {
    return runtimeConfig?.ccoPanelEnabled ?? true;
  }
  if (context === 'planning-assistant') {
    return runtimeConfig?.planningAssistantEnabled ?? true;
  }
  if (context === 'beta-reports') {
    return runtimeConfig?.betaReportsEnabled ?? true;
  }
  if (context === 'liquidation') {
    return runtimeConfig?.liquidationEnabled ?? true;
  }
  if (context === 'otb-execution') {
    return runtimeConfig?.otbExecutionEnabled ?? true;
  }
  if (context === 'rules-engine') {
    return runtimeConfig?.rulesEngineEnabled ?? true;
  }
  return true;
}
export function isDemoPreviewMode() {
  return isPreviewMode();
}
export function setPreviewDemoConfig(config: DemoConfig) {
  runtimeConfig = config;
  runtimeConfigMeta =
    config && (config.updatedAt || config.version != null)
      ? {
          updatedAt: config.updatedAt ?? null,
          version: config.version ?? null,
        }
      : null;
  sheetCache.clear();
}
